import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { isOnboardingDemoPopulation } from '@/components/onboarding/onboardingDemoReport';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import {
  GREENFIELD_SHARING,
  HIDDEN,
  OLAPS_ONLY,
  PARTNER_POPULATION_RULE,
  PARTNER_RULE,
  POPULATION_RULE,
  SHARING,
  SHARING_TYPES,
  SharingType,
} from '@/constants/data_shares';
import { isGoogleOrCsvSource } from '@/constants/data_sources';
import { captureException } from '@/errors';
import { useBillingStore } from '@/stores/BillingStore';
import { usePopulationsStore } from '@/stores/PopulationsStore';
import { useRootStore } from '@/stores/RootStore';
import { initStore } from '@/stores/store-utils';
import {
  Assignments,
  DataSharingRule,
  DataSharingVisibility,
  IncomingDataShare,
  IncomingGreenfieldShare,
  IncomingSharingRule,
  OutgoingDataShare,
  OutgoingSharingRule,
  OutgoingSharingRuleMap,
  Share,
  SharedField,
  SharedFieldWithPartnerOrgId,
} from '@/types/data_shares';
import { Population } from '@/types/populations';
import { SharingLevel } from '@/types/proposal';

export type DataShareItem = {
  id: number | null;
  partnerId: number | null;
  feedId: number;
  sourceId?: number;
  presetParentId: number; // feedId or sourceId
  populationId: number;
  populationName: string;
  visibility: SharingType;
  presetId?: number | null;
  integrationType?: string;
};

export type PopulationToPopulationMap = Record<
  number,
  Record<number, number[]>
>;

/** Population id -> visibility of the first rule for it, as Array.find picks. */
function firstRuleByPopulation(rules: OutgoingSharingRule[]) {
  const byPopulation = new Map<number, OutgoingSharingRule>();
  for (const rule of rules) {
    if (!byPopulation.has(rule.population_id)) {
      byPopulation.set(rule.population_id, rule);
    }
  }
  return byPopulation;
}

function indexFirstRuleByKey(
  rulesByKey: Record<number, OutgoingSharingRule[]> = {},
) {
  return new Map(
    Object.entries(rulesByKey).map(([key, rules]) => [
      Number(key),
      firstRuleByPopulation(rules),
    ]),
  );
}

export const useDataSharesStore = defineStore('DataSharesStore', () => {
  const allIncomingSharedFields = ref<
    Record<number, SharedFieldWithPartnerOrgId>
  >({});
  const outgoingDataShares = ref<OutgoingDataShare[]>([]);
  const incomingDataShares = ref<IncomingDataShare[]>([]);
  const outgoingSharingRules = ref<OutgoingSharingRule[]>([]);
  const incomingSharingRules = ref<IncomingSharingRule[]>([]);
  const incomingGreenfieldShares = ref<IncomingGreenfieldShare[]>([]);
  const isGreenfieldSharingBlocked = ref(false);

  // Editing state for data shares (scoped by session key)
  const editingDataShares = ref<Record<string, DataShareItem[]>>({});
  const initialEditingDataShares = ref<Record<string, DataShareItem[]>>({});

  // Population-to-population editing state (scoped by session key)
  const currentPopToPopMap = ref<Record<string, PopulationToPopulationMap>>({});
  const initialPopToPopMap = ref<Record<string, PopulationToPopulationMap>>({});

  // Bulk operation payload (scoped by session key)
  const bulkPayload = ref<Record<string, Assignments>>({});
  const shareIdsToDelete = ref<Record<string, Record<number, number[]>>>({});

  const billingStore = useBillingStore();
  const rootStore = useRootStore();

  async function fetchDataShares() {
    const [
      outgoingShareResponse,
      incomingShareResponse,
      outgoingSharingRuleResponse,
      incomingSharingRuleResponse,
    ] = await Promise.all([
      crossbeamApi.GET('/v0.1/outgoing-data-shares'),
      crossbeamApi.GET('/v0.1/incoming-data-shares'),
      crossbeamApi.GET('/v0.1/outgoing-sharing-rules'),
      crossbeamApi.GET('/v0.1/incoming-sharing-rules'),
    ]);

    const err =
      outgoingShareResponse.error ||
      incomingShareResponse.error ||
      outgoingSharingRuleResponse.error ||
      incomingSharingRuleResponse.error;

    if (err) {
      captureException(err);
      return;
    }

    outgoingDataShares.value = outgoingShareResponse.data?.items;
    incomingDataShares.value = incomingShareResponse.data?.items;
    outgoingSharingRules.value = outgoingSharingRuleResponse.data?.items;
    incomingSharingRules.value = incomingSharingRuleResponse.data?.items;
    allIncomingSharedFields.value = incomingDataShares.value.reduce(
      (
        sharedFields: Record<number, SharedFieldWithPartnerOrgId>,
        incomingDataShare: IncomingDataShare,
      ) => {
        incomingDataShare.shared_fields.forEach((share) => {
          sharedFields[share.source_field_id] = {
            ...share,
            partner_org_id: incomingDataShare.partner_org_id,
          };
        });
        return sharedFields;
      },
      {},
    );
  }

  async function fetchIncomingGreenfieldShares() {
    const incomingGreenfieldResponse = await crossbeamApi.GET(
      '/v0.1/incoming-population-visibility',
      {
        params: {
          query: {
            visibility: 'greenfield_full',
          },
        },
      },
    );

    incomingGreenfieldShares.value = (
      incomingGreenfieldResponse.data?.items ?? []
    ).map((item) => ({
      ...item,
      partner_population_ids: item.partner_population_ids ?? [],
    }));
  }

  const { initError, ready, readySync, running, refresh } = initStore(
    async () => {
      await Promise.all([fetchDataShares(), fetchIncomingGreenfieldShares()]);

      // Fetch greenfield sharing controls for enterprise non-admin users
      await billingStore.readySync;
      const { isAdmin } = useAuth();
      // Ensure currentAuth is loaded before checking isAdmin to avoid race condition
      // where sessionLoaded is true but currentAuth hasn't been set yet
      if (
        billingStore.isEnterpriseTier &&
        rootStore.currentAuth &&
        !isAdmin.value
      ) {
        const { data, error } = await crossbeamApi.GET(
          '/v0.1/organization/settings/sharing-controls',
        );

        if (error) {
          captureException(error);
        }

        if (data) {
          isGreenfieldSharingBlocked.value = data.is_greenfield_sharing_blocked;
        }
      }
    },
  );

  refresh({ useCache: true });

  const outgoingSharingRuleMap = computed<OutgoingSharingRuleMap>(() => {
    return outgoingSharingRules.value.reduce(
      (lookup: OutgoingSharingRuleMap, share: OutgoingSharingRule) => {
        const rule = share.rule_type;
        if (!lookup[rule]) lookup[rule] = {};
        switch (rule) {
          case POPULATION_RULE:
            if (!lookup[rule]?.[share.population_id])
              lookup[rule][share.population_id] = [];
            lookup[rule][share.population_id]?.push(share);
            break;

          case PARTNER_RULE:
            if (!share.partner_org_id) break;

            if (!lookup[rule]?.[share.partner_org_id])
              lookup[rule][share.partner_org_id] = [];
            lookup[rule][share.partner_org_id]?.push(share);
            break;

          case PARTNER_POPULATION_RULE:
            if (!share.partner_population_id) break;

            if (!lookup[rule]?.[share.partner_population_id])
              lookup[rule][share.partner_population_id] = [];
            lookup[rule][share.partner_population_id]?.push(share);
            break;

          default:
            break;
        }
        return lookup;
      },
      {},
    );
  });

  const partnerRulesByPopulation = computed(() => {
    const map = new Map<number, OutgoingSharingRule[]>();

    outgoingSharingRules.value.forEach((rule) => {
      if (rule.rule_type === PARTNER_RULE) {
        if (!map.has(rule.population_id)) {
          map.set(rule.population_id, []);
        }
        map.get(rule.population_id)?.push(rule);
      }
    });

    return map;
  });

  const partnerPopulationRulesByPopulation = computed(() => {
    const map = new Map<number, OutgoingSharingRule[]>();

    outgoingSharingRules.value.forEach((rule) => {
      if (rule.rule_type === PARTNER_POPULATION_RULE) {
        if (!map.has(rule.population_id)) {
          map.set(rule.population_id, []);
        }
        map.get(rule.population_id)?.push(rule);
      }
    });

    return map;
  });

  /**
   * Override rules for one of our populations with a partner. The index is
   * built once per rule change and the lookup closes over it, so a caller
   * resolving a cell per partner and population reads the computed once.
   * Derived from partnerPopulationRulesByPopulation rather than
   * rescanning every rule, so what counts as an override is encoded in one
   * place. Why overrides are listed and not folded into a level: see
   * OutgoingPopulationSharingLevel.
   */
  const outgoingOverridesLookup = computed(() => {
    const rulesByPopulation = new Map<
      number,
      Map<number, OutgoingSharingRule[]>
    >();
    partnerPopulationRulesByPopulation.value.forEach((rules, populationId) => {
      const rulesByPartner = new Map<number, OutgoingSharingRule[]>();
      rules.forEach((rule) => {
        // Never null from the API; the guard only narrows the type for the key.
        if (!rule.partner_org_id) return;
        const partnerRules = rulesByPartner.get(rule.partner_org_id) ?? [];
        partnerRules.push(rule);
        rulesByPartner.set(rule.partner_org_id, partnerRules);
      });
      rulesByPopulation.set(populationId, rulesByPartner);
    });
    return function lookupOutgoingOverrides(
      populationId: number,
      partnerOrgId: number,
    ): OutgoingSharingRule[] {
      return rulesByPopulation.get(populationId)?.get(partnerOrgId) ?? [];
    };
  });

  const outgoingOverrideCountLookup = computed(() => {
    const lookupOverrides = outgoingOverridesLookup.value;
    return function lookupOutgoingOverrideCount(
      populationId: number,
      partnerOrgId: number,
    ): number {
      return lookupOverrides(populationId, partnerOrgId).length;
    };
  });

  const incomingSharingRulesMap = computed(() => {
    const map = {
      byPartnerPopId: new Map<number, IncomingSharingRule[]>(),
      byPopId: new Map<number, IncomingSharingRule[]>(),
      byPartnerOrgId: new Map<number, IncomingSharingRule[]>(),
    };

    incomingSharingRules.value.forEach((rule) => {
      if (rule.partner_population_id) {
        if (!map.byPartnerPopId.has(rule.partner_population_id)) {
          map.byPartnerPopId.set(rule.partner_population_id, []);
        }
        map.byPartnerPopId.get(rule.partner_population_id)?.push(rule);
      }

      if (rule.population_id) {
        if (!map.byPopId.has(rule.population_id)) {
          map.byPopId.set(rule.population_id, []);
        }
        map.byPopId.get(rule.population_id)?.push(rule);
      }

      if (rule.partner_org_id) {
        if (!map.byPartnerOrgId.has(rule.partner_org_id)) {
          map.byPartnerOrgId.set(rule.partner_org_id, []);
        }
        map.byPartnerOrgId.get(rule.partner_org_id)?.push(rule);
      }
    });

    return map;
  });

  /**
   * Shared fields of the first non-override incoming rule for a partner
   * population, as getIncomingSharingRules({ partnerPopulationId,
   * isOverride: false })[0] answers it, indexed once per rule change.
   */
  const incomingSharedFieldsLookup = computed(() => {
    const fieldsByPartnerPopulation = new Map<number, SharedField[]>();
    incomingSharingRules.value.forEach((rule) => {
      if (
        rule.is_override === false &&
        rule.partner_population_id &&
        !fieldsByPartnerPopulation.has(rule.partner_population_id)
      ) {
        fieldsByPartnerPopulation.set(
          rule.partner_population_id,
          rule.shared_fields,
        );
      }
    });
    return function lookupIncomingSharedFields(
      partnerPopulationId: number,
    ): SharedField[] {
      return fieldsByPartnerPopulation.get(partnerPopulationId) ?? [];
    };
  });

  const incomingSharedFieldCountLookup = computed(() => {
    const lookupFields = incomingSharedFieldsLookup.value;
    return function lookupIncomingSharedFieldCount(
      partnerPopulationId: number,
    ): number {
      return lookupFields(partnerPopulationId).length;
    };
  });

  const greenfieldSharesLookup = computed<Record<number, number[]>>(() => {
    return incomingGreenfieldShares.value.reduce(
      (lookup: Record<number, number[]>, share: IncomingGreenfieldShare) => {
        lookup[share.partner_organization_id] = share.partner_population_ids;
        return lookup;
      },
      {},
    );
  });

  const isSharingDataWithPartners = computed(() => {
    const populationsStore = usePopulationsStore();
    function isUserPopulationShare(share: { population_id: number }) {
      return !isOnboardingDemoPopulation(
        populationsStore.getPopulationById(share.population_id),
      );
    }
    const visibleDefaultShares = outgoingSharingRules.value.filter(
      (dataShare) =>
        dataShare.visibility !== HIDDEN && isUserPopulationShare(dataShare),
    );
    return (
      visibleDefaultShares.length > 0 ||
      outgoingDataShares.value.some(isUserPopulationShare)
    );
  });

  /**
   * Generate a session key for scoped editing state
   */
  function getSessionKey(partnerId: number | null): string {
    return partnerId !== null ? `partner_${partnerId}` : 'default';
  }

  /**
   * Initialize editing session for a partner
   */
  function initializeEditingSession(
    partnerId: number | null,
    populations: Population[],
  ) {
    const sessionKey = getSessionKey(partnerId);

    // Get active data shares from store
    const activeShares = getActiveDataSharesForPartner(partnerId, populations);
    editingDataShares.value[sessionKey] = activeShares;
    initialEditingDataShares.value[sessionKey] = [...activeShares];

    // Initialize population to population settings
    const popToPopMap: PopulationToPopulationMap = {};
    populations.forEach((population) => {
      const popWithSettings = population as Population & {
        populationToPopulationSettings?: {
          partnerPopulation?: { id: number };
          selectedSourceFieldIds?: number[];
        }[];
      };
      if (!popWithSettings.populationToPopulationSettings?.length) {
        return;
      }
      if (!popToPopMap[population.id]) {
        popToPopMap[population.id] = {};
      }
      popWithSettings.populationToPopulationSettings.forEach((setting) => {
        if (!setting.partnerPopulation?.id) {
          return;
        }
        const partnerPopulationId = setting.partnerPopulation.id;
        const popMap = popToPopMap[population.id];
        if (popMap) {
          popMap[partnerPopulationId] = setting.selectedSourceFieldIds || [];
        }
      });
    });

    currentPopToPopMap.value[sessionKey] = popToPopMap;
    initialPopToPopMap.value[sessionKey] = { ...popToPopMap };

    // Initialize bulk payload
    bulkPayload.value[sessionKey] = { data_shares: [] };
    shareIdsToDelete.value[sessionKey] = {};
  }

  /**
   * Clean up editing session
   */
  function cleanupEditingSession(partnerId: number | null) {
    const sessionKey = getSessionKey(partnerId);

    const {
      [sessionKey]: _sessionEditingDataShares,
      ...restEditingDataShares
    } = editingDataShares.value;
    editingDataShares.value = restEditingDataShares;
    const {
      [sessionKey]: _sessionInitialEditingDataShares,
      ...restInitialEditingDataShares
    } = initialEditingDataShares.value;
    initialEditingDataShares.value = restInitialEditingDataShares;
    const {
      [sessionKey]: _sessionCurrentPopToPopMap,
      ...restCurrentPopToPopMap
    } = currentPopToPopMap.value;
    currentPopToPopMap.value = restCurrentPopToPopMap;
    const {
      [sessionKey]: _sessionInitialPopToPopMap,
      ...restInitialPopToPopMap
    } = initialPopToPopMap.value;
    initialPopToPopMap.value = restInitialPopToPopMap;
    const { [sessionKey]: _sessionBulkPayload, ...restBulkPayload } =
      bulkPayload.value;
    bulkPayload.value = restBulkPayload;
    const { [sessionKey]: _sessionShareIdsToDelete, ...restShareIdsToDelete } =
      shareIdsToDelete.value;
    shareIdsToDelete.value = restShareIdsToDelete;
  }

  /**
   * Update a population-to-population override
   */
  function updatePopToPopOverride(
    partnerId: number | null,
    populationId: number,
    partnerPopulationId: number,
    fields: number[],
  ) {
    const sessionKey = getSessionKey(partnerId);

    // If fields is empty, remove the override instead of saving it
    if (fields.length === 0) {
      if (currentPopToPopMap.value[sessionKey]?.[populationId]) {
        const { [partnerPopulationId]: _, ...remainingOverrides } =
          currentPopToPopMap.value[sessionKey][populationId];

        // If no more overrides for this population, remove the population entry
        if (Object.keys(remainingOverrides).length === 0) {
          const { [populationId]: __, ...remainingPopulations } =
            currentPopToPopMap.value[sessionKey];
          currentPopToPopMap.value[sessionKey] = remainingPopulations;
        } else {
          currentPopToPopMap.value[sessionKey][populationId] =
            remainingOverrides;
        }
      }
      return;
    }

    if (!currentPopToPopMap.value[sessionKey]) {
      currentPopToPopMap.value[sessionKey] = {};
    }
    if (!currentPopToPopMap.value[sessionKey][populationId]) {
      currentPopToPopMap.value[sessionKey][populationId] = {};
    }
    currentPopToPopMap.value[sessionKey][populationId][partnerPopulationId] =
      fields;
  }

  /**
   * Delete a population-to-population override
   */
  function deletePopToPopOverride(
    partnerId: number | null,
    shareId: number | null,
    partnerPopulationId: number,
    populationId: number,
  ) {
    const sessionKey = getSessionKey(partnerId);
    if (shareId) {
      if (!shareIdsToDelete.value[sessionKey]) {
        shareIdsToDelete.value[sessionKey] = {};
      }
      if (!shareIdsToDelete.value[sessionKey][populationId]) {
        shareIdsToDelete.value[sessionKey][populationId] = [];
      }
      shareIdsToDelete.value[sessionKey][populationId].push(shareId);

      const updatedMap = { ...currentPopToPopMap.value[sessionKey] };
      if (updatedMap[populationId]) {
        const { [partnerPopulationId]: _, ...updatedInnerMap } =
          updatedMap[populationId];
        if (Object.keys(updatedInnerMap).length === 0) {
          const { [populationId]: __, ...restUpdatedMap } = updatedMap;
          currentPopToPopMap.value[sessionKey] = restUpdatedMap;
        } else {
          updatedMap[populationId] = updatedInnerMap;
          currentPopToPopMap.value[sessionKey] = updatedMap;
        }
      }
    }
  }

  /**
   * Save all changes for a partner (bulk update)
   */
  async function bulkSaveChanges(partnerId: number | null) {
    const sessionKey = getSessionKey(partnerId);
    const payload = bulkPayload.value[sessionKey];
    const sharesToDelete = shareIdsToDelete.value[sessionKey] || {};
    const popToPopSettings = currentPopToPopMap.value[sessionKey] || {};

    // Delete shares marked for deletion
    if (Object.keys(sharesToDelete).length > 0) {
      const deletionPromises = Object.entries(sharesToDelete).flatMap(
        ([_, ids]) =>
          ids.map((id: number) =>
            crossbeamApi.DELETE('/v0.1/outgoing-sharing-rules/{id}', {
              params: { path: { id } },
            }),
          ),
      );
      await Promise.all(deletionPromises);
    }
    // Save data shares
    if (payload && payload.data_shares.length > 0) {
      const { error } = await crossbeamApi.POST(
        '/v0.1/data-share-assignments',
        {
          body: {
            ...payload,
            partner_org_id: partnerId,
          },
        },
      );
      if (error) {
        throw error;
      }
    }
    // Save population-to-population overrides via bulk endpoint
    if (partnerId !== null) {
      const overridePromises: Promise<unknown>[] = [];
      Object.keys(popToPopSettings).forEach((populationIdStr) => {
        const populationId = Number(populationIdStr);
        const partnerOverrides = popToPopSettings[populationId];

        if (!partnerOverrides) return;

        const dataShares = Object.entries(partnerOverrides)
          .filter(
            ([, selectedSourceFields]) =>
              Array.isArray(selectedSourceFields) &&
              selectedSourceFields.length > 0,
          )
          .map(([partnerPopulationId, selectedSourceFields]) => ({
            receiving_population_id: parseInt(partnerPopulationId),
            visibility: SHARING as SharingType,
            source_field_ids: selectedSourceFields,
          }));

        if (dataShares.length === 0) return;

        overridePromises.push(
          crossbeamApi.POST(
            '/v0.1/data-share-assignments/populations/{population_id}/population-override-partners/{partner_org_id}',
            {
              body: { data_shares: dataShares },
              params: {
                path: {
                  population_id: populationId,
                  partner_org_id: partnerId,
                },
              },
            },
          ),
        );
      });

      await Promise.all(overridePromises);
    }

    // Clear editing state
    shareIdsToDelete.value[sessionKey] = {};
    if (payload) {
      payload.data_shares = [];
    }

    // Refresh the store
    await refresh();

    // Return saved shares for tracking purposes
    return payload?.data_shares || [];
  }

  /* getters */

  /**
   * Get default (population-level) data shares as a map by population ID
   */
  function getDefaultDataSharesMap(
    populations: Population[],
  ): Record<number, DataShareItem> {
    const defaultRulesMap: Record<number, DataShareItem> = {};
    outgoingSharingRules.value
      .filter((rule: OutgoingSharingRule) => rule.rule_type === 'population')
      .forEach((rule: OutgoingSharingRule) => {
        defaultRulesMap[rule.population_id] = toDataShareItem(
          rule,
          populations,
        );
      });
    return defaultRulesMap;
  }

  /**
   * Get active data shares for a specific partner (defaults + partner overrides)
   * Returns only non-hidden shares
   */
  function getActiveDataSharesForPartner(
    partnerId: number | null,
    populations: Population[],
  ): DataShareItem[] {
    const defaultRulesMap = getDefaultDataSharesMap(populations);

    const activeRulesMap: Record<number, DataShareItem> = {
      ...defaultRulesMap,
    };

    if (partnerId !== null) {
      outgoingSharingRules.value
        .filter(
          (rule: OutgoingSharingRule) =>
            rule.rule_type === 'partner' && rule.partner_org_id === partnerId,
        )
        .forEach((rule: OutgoingSharingRule) => {
          activeRulesMap[rule.population_id] = toDataShareItem(
            rule,
            populations,
          );
        });
    }

    return Object.values(activeRulesMap).filter(
      (share) => share.visibility !== SHARING_TYPES.HIDDEN,
    );
  }

  function getAllIncomingDataShares({
    partnerOrgId,
    partnerPopulationId,
  }: {
    partnerOrgId: number;
    partnerPopulationId: number;
  }): (IncomingDataShare | IncomingSharingRule)[] {
    const matchingShare = (
      share: IncomingDataShare | IncomingSharingRule,
    ): boolean =>
      share.partner_org_id === partnerOrgId &&
      share.partner_population_id === partnerPopulationId;
    const incomingRules = incomingSharingRules.value.filter(matchingShare);
    const incomingShares = incomingDataShares.value.filter(matchingShare);
    return [...incomingRules, ...incomingShares];
  }

  function getIncomingDataShare({
    populationId,
    partnerPopulationId,
  }: {
    populationId: number;
    partnerPopulationId: number;
  }): IncomingDataShare | undefined {
    return incomingDataShares.value.find((dataShare) => {
      return (
        dataShare.partner_population_id === partnerPopulationId &&
        dataShare.population_ids.includes(populationId)
      );
    });
  }

  function getDataShareById(
    id: number,
  ):
    | IncomingDataShare
    | OutgoingDataShare
    | IncomingSharingRule
    | OutgoingSharingRule
    | undefined {
    return (
      incomingDataShares.value.find((share) => share.id === id) ||
      outgoingDataShares.value.find((share) => share.id === id) ||
      outgoingSharingRules.value.find((share) => share.id === id) ||
      incomingSharingRules.value.find((share) => share.id === id)
    );
  }

  function getDataShareWithPopulationInfo(id: number) {
    const populationsStore = usePopulationsStore();
    const dataShare = getDataShareById(id);
    if (!dataShare) return {};
    let partnerPopulation;
    let ourPopulation;
    if (isIncomingShare(dataShare)) {
      partnerPopulation = populationsStore.getPartnerPopulationById(
        dataShare.partner_population_id,
      );
    }
    if (isOutgoingShare(dataShare)) {
      ourPopulation = populationsStore.getPopulationById(
        dataShare.population_id,
      );
    }
    return { partnerPopulation, ourPopulation, dataShare };
  }

  /**
   * Get incoming sharing rules with optimized indexed lookups
   * Uses incomingSharingRulesMap for O(1) lookups when possible
   */
  function getIncomingSharingRules({
    partnerOrgId,
    populationId,
    partnerPopulationId,
    isOverride = false,
  }: {
    partnerOrgId?: number;
    populationId?: number;
    partnerPopulationId?: number;
    isOverride?: boolean | null;
  }): IncomingSharingRule[] {
    let candidates: IncomingSharingRule[] = [];

    // Use indexed map when possible for O(1) lookup
    if (partnerPopulationId) {
      candidates =
        incomingSharingRulesMap.value.byPartnerPopId.get(partnerPopulationId) ||
        [];
    } else if (populationId) {
      candidates =
        incomingSharingRulesMap.value.byPopId.get(populationId) || [];
    } else if (partnerOrgId) {
      candidates =
        incomingSharingRulesMap.value.byPartnerOrgId.get(partnerOrgId) || [];
    } else {
      // No indexable field, fall back to full array
      candidates = incomingSharingRules.value;
    }

    // Apply additional filters to the smaller candidate set
    return candidates.filter((dataShare: IncomingSharingRule) => {
      const filters = [];
      if (isOverride !== null)
        filters.push(dataShare.is_override === isOverride);
      if (partnerOrgId) filters.push(dataShare.partner_org_id === partnerOrgId);
      if (populationId) filters.push(dataShare.population_id === populationId);
      if (partnerPopulationId)
        filters.push(dataShare.partner_population_id === partnerPopulationId);
      return filters.length > 0 && filters.every((filter) => !!filter);
    });
  }

  /**
   * Get outgoing sharing rules with optimized indexed lookups
   * Uses outgoingSharingRuleMap for O(1) lookups when possible
   */
  function getOutgoingSharingRules({
    ruleType,
    partnerOrgId,
    populationId,
    partnerPopulationId,
    visibility,
  }: {
    partnerOrgId?: number;
    populationId?: number;
    partnerPopulationId?: number;
    ruleType?: DataSharingRule;
    visibility?: DataSharingVisibility;
  }): OutgoingSharingRule[] {
    let candidates: OutgoingSharingRule[] = [];

    // Use indexed map when we have specific IDs for O(1) lookup
    // This covers the critical hot paths in loops
    if (ruleType === POPULATION_RULE && populationId) {
      candidates =
        outgoingSharingRuleMap.value[POPULATION_RULE]?.[populationId] || [];
    } else if (ruleType === PARTNER_RULE && partnerOrgId) {
      candidates =
        outgoingSharingRuleMap.value[PARTNER_RULE]?.[partnerOrgId] || [];
    } else if (ruleType === PARTNER_RULE && populationId) {
      // OPTIMIZATION: Secondary index for common query pattern
      candidates = partnerRulesByPopulation.value.get(populationId) || [];
    } else if (ruleType === PARTNER_POPULATION_RULE && partnerPopulationId) {
      candidates =
        outgoingSharingRuleMap.value[PARTNER_POPULATION_RULE]?.[
          partnerPopulationId
        ] || [];
    } else if (ruleType === PARTNER_POPULATION_RULE && populationId) {
      // OPTIMIZATION: Secondary index for common query pattern with populationId
      candidates =
        partnerPopulationRulesByPopulation.value.get(populationId) || [];
    } else {
      // Fall back to full array to preserve original order
      // This path is rarely hit (only for queries without specific IDs)
      candidates = outgoingSharingRules.value;
    }

    // Apply additional filters to the smaller candidate set
    return candidates.filter((dataShare: OutgoingSharingRule) => {
      const filters = [];
      if (ruleType) filters.push(dataShare.rule_type === ruleType);
      if (partnerOrgId) {
        filters.push(
          dataShare.partner_org_id === partnerOrgId ||
            dataShare.rule_type === POPULATION_RULE,
        );
      }
      if (populationId) filters.push(dataShare.population_id === populationId);
      if (partnerPopulationId) {
        filters.push(dataShare.partner_population_id === partnerPopulationId);
      }
      if (visibility) filters.push(dataShare.visibility === visibility);
      return filters.every((filter) => !!filter);
    });
  }

  function getSharedFieldBySourceFieldId(
    sourceFieldId: number,
  ): SharedFieldWithPartnerOrgId | undefined {
    return allIncomingSharedFields.value[sourceFieldId];
  }

  function isSharingPopulationWithPartner(
    populationId: number,
    partnerOrgId: number,
  ): boolean {
    let partnerOverride;
    if (outgoingSharingRuleMap.value[PARTNER_RULE]?.[partnerOrgId]) {
      partnerOverride = outgoingSharingRuleMap.value[PARTNER_RULE][
        partnerOrgId
      ].find((share) => share.population_id === populationId);
    }
    if (partnerOverride) return partnerOverride.visibility !== HIDDEN;

    let generalPopulationSharingRule;
    if (outgoingSharingRuleMap.value[POPULATION_RULE]?.[populationId]) {
      // there will only ever be one general population rule per population, no need to .find
      generalPopulationSharingRule =
        outgoingSharingRuleMap.value[POPULATION_RULE][populationId][0];
    }
    if (generalPopulationSharingRule)
      return generalPopulationSharingRule.visibility !== HIDDEN;
    return false;
  }

  function isSharingPopulationWithPartnerPopulation(
    populationId: number,
    partnerOrgId: number,
    partnerPopulationId: number,
  ): boolean {
    // Check for a share rule in order of highest rule precedence:
    // partner population override, partner override, general population rule
    // I suspect rule_type must be partner_population if the previous constraints are met
    let partnerPopulationOverride;
    if (
      outgoingSharingRuleMap.value[PARTNER_POPULATION_RULE]?.[
        partnerPopulationId
      ]
    ) {
      partnerPopulationOverride = outgoingSharingRuleMap.value[
        PARTNER_POPULATION_RULE
      ][partnerPopulationId].find(
        (share) => share.population_id === populationId,
      );
    }
    if (partnerPopulationOverride)
      return partnerPopulationOverride.visibility !== HIDDEN;

    let partnerOverride;
    if (outgoingSharingRuleMap.value[PARTNER_RULE]?.[partnerOrgId]) {
      partnerOverride = outgoingSharingRuleMap.value[PARTNER_RULE][
        partnerOrgId
      ].find((share) => share.population_id === populationId);
    }
    if (partnerOverride) return partnerOverride.visibility !== HIDDEN;

    let generalPopulationSharingRule;
    if (outgoingSharingRuleMap.value[POPULATION_RULE]?.[populationId]) {
      // there will only ever be one general population rule per population, no need to .find
      generalPopulationSharingRule =
        outgoingSharingRuleMap.value[POPULATION_RULE][populationId][0];
    }
    if (generalPopulationSharingRule)
      return generalPopulationSharingRule.visibility !== HIDDEN;
    return false;
  }

  /**
   * The rule getEffectiveVisibilityForPopulationWithPartner reads, bound to
   * plain Maps built once per rule change, so a caller resolving a cell per
   * partner and population reads it once instead of searching the reactive
   * rules on every call. The first matching rule wins, as with Array.find.
   */
  const outgoingRuleLookup = computed(() => {
    const ruleMap = outgoingSharingRuleMap.value;
    const byPartnerPopulation = indexFirstRuleByKey(
      ruleMap[PARTNER_POPULATION_RULE],
    );
    const byPartner = indexFirstRuleByKey(ruleMap[PARTNER_RULE]);
    const populationDefaults = firstRuleByPopulation(
      Object.values(ruleMap[POPULATION_RULE] ?? {}).flat(),
    );

    return function lookupOutgoingRule(
      populationId: number,
      partnerOrgId: number,
      partnerPopulationId?: number,
    ): OutgoingSharingRule | undefined {
      const partnerPopulationRule =
        partnerPopulationId != null
          ? byPartnerPopulation.get(partnerPopulationId)?.get(populationId)
          : undefined;
      return (
        partnerPopulationRule ??
        byPartner.get(partnerOrgId)?.get(populationId) ??
        populationDefaults.get(populationId)
      );
    };
  });

  const outgoingVisibilityLookup = computed(() => {
    const lookupRule = outgoingRuleLookup.value;
    return function lookupOutgoingVisibility(
      populationId: number,
      partnerOrgId: number,
      partnerPopulationId?: number,
    ): SharingType {
      const rule = lookupRule(populationId, partnerOrgId, partnerPopulationId);
      return (rule?.visibility as SharingType | undefined) ?? HIDDEN;
    };
  });

  /**
   * Returns the effective visibility for our population with a partner (org or
   * specific partner population), following the same rule precedence as
   * isSharingPopulationWithPartnerPopulation. Used to determine if we only
   * share overlap counts (OLAPS_ONLY) with the partner.
   *
   * No rule anywhere in the chain means the partner sees nothing, which is
   * HIDDEN - the same terminal answer isSharingPopulationWithPartnerPopulation
   * gives by returning false. This answers what the partner sees, not whether
   * anything was configured; the By Populations tab needs that second question
   * and reads the rules directly.
   */
  function getEffectiveVisibilityForPopulationWithPartner(
    populationId: number,
    partnerOrgId: number,
    partnerPopulationId?: number,
  ): SharingType {
    return outgoingVisibilityLookup.value(
      populationId,
      partnerOrgId,
      partnerPopulationId,
    );
  }

  function somePopulationVisibilityMatches(
    populationIds: number[],
    partnerOrgId: number,
    partnerPopulationIds: number[] | undefined,
    predicate: (visibility: SharingType) => boolean,
  ): boolean {
    const partnerPops = partnerPopulationIds?.length
      ? partnerPopulationIds
      : [undefined];
    const lookupVisibility = outgoingVisibilityLookup.value;
    for (const ourPopId of populationIds) {
      for (const partnerPopId of partnerPops) {
        const visibility = lookupVisibility(
          ourPopId,
          partnerOrgId,
          partnerPopId,
        );
        if (predicate(visibility)) return true;
      }
    }
    return false;
  }

  function isSharedVisibility(v: SharingType): boolean {
    return v === SHARING || v === GREENFIELD_SHARING;
  }

  function isCountsOnlyVisibility(v: SharingType): boolean {
    return v === OLAPS_ONLY;
  }

  /**
   * True when we only share overlap counts (no record-level data) with the
   * given partner org for the given populations. Used to block list external
   * sharing when the partner only has overlap counts.
   */
  function isOnlyOverlapCountsSharedWithPartner(
    populationIds: number[],
    partnerOrgId: number,
    partnerPopulationIds?: number[],
  ): boolean {
    if (populationIds.length === 0) return true;
    return !somePopulationVisibilityMatches(
      populationIds,
      partnerOrgId,
      partnerPopulationIds,
      isSharedVisibility,
    );
  }

  /**
   * True when the report mixes "counts only" and record-level sharing for the
   * given partner — i.e. at least one population is shared and at least one is
   * counts-only. Used to show a warning that some accounts won't be visible.
   */
  function hasMixedSharingWithPartner(
    populationIds: number[],
    partnerOrgId: number,
    partnerPopulationIds?: number[],
  ): boolean {
    if (populationIds.length === 0) return false;
    let hasShared = false;
    let hasCountsOnly = false;
    return somePopulationVisibilityMatches(
      populationIds,
      partnerOrgId,
      partnerPopulationIds,
      (v) => {
        if (isSharedVisibility(v)) hasShared = true;
        else if (isCountsOnlyVisibility(v)) hasCountsOnly = true;
        return hasShared && hasCountsOnly;
      },
    );
  }

  function populationSharesToServer(
    populationShares: {
      population_id: number;
      sharing_level: SharingLevel;
    }[],
  ) {
    return populationShares.map((populationShare) => {
      let sharingLevel = populationShare.sharing_level;
      const populationSharingSetting = getOutgoingSharingRules({
        ruleType: POPULATION_RULE,
      }).find(
        (setting) => setting.population_id === populationShare.population_id,
      ) || { visibility: HIDDEN };
      if (populationSharingSetting.visibility === sharingLevel)
        sharingLevel = 'use_defaults';
      return { ...populationShare, sharing_level: sharingLevel };
    });
  }

  /**
   * Transform an OutgoingSharingRule to a DataShareItem using population data
   */
  function toDataShareItem(
    rule: OutgoingSharingRule,
    populations: Population[],
  ): DataShareItem {
    const pop = populations.find(
      (population) => population.id === rule.population_id,
    );
    const isGoogleOrCsv = pop?.integration_type
      ? isGoogleOrCsvSource(pop.integration_type)
      : false;
    return {
      id: rule.id,
      partnerId: rule.partner_org_id,
      populationId: pop?.id ?? rule.population_id,
      populationName: pop?.name ?? '',
      integrationType: pop?.integration_type,
      visibility: rule.visibility as SharingType,
      presetId: rule.data_share_presets_id,
      feedId: pop?.feed_id ?? 0,
      sourceId: isGoogleOrCsv ? pop?.source_id : undefined,
      presetParentId: isGoogleOrCsv
        ? (pop?.source_id ?? 0)
        : (pop?.feed_id ?? 0),
    };
  }

  /* actions */
  async function removeAndDeleteDataShare(id: number): Promise<void> {
    removeDataShare(id);
    const { error } = await crossbeamApi.DELETE(
      '/v0.1/outgoing-sharing-rules/{id}',
      {
        params: {
          path: {
            id,
          },
        },
      },
    );

    if (error) {
      refresh();
      throw error;
    }
  }

  function removeDataShare(id: number): void {
    outgoingDataShares.value = outgoingDataShares.value.filter(
      (ds) => ds.id !== id,
    );
    incomingDataShares.value = incomingDataShares.value.filter(
      (ds) => ds.id !== id,
    );
    outgoingSharingRules.value = outgoingSharingRules.value.filter(
      (ds) => ds.id !== id,
    );
  }

  function removeDataShareByPopulationId(populationId: number): void {
    outgoingDataShares.value = outgoingDataShares.value.filter(
      (ds) => ds.population_id !== populationId,
    );
    incomingDataShares.value = incomingDataShares.value
      .map((ds) => {
        const popIds = ds.population_ids.filter((id) => id !== populationId);
        return { ...ds, population_ids: popIds };
      })
      .filter((ds) => !!ds.population_ids.length);
    outgoingSharingRules.value = outgoingSharingRules.value.filter(
      (ds) => ds.population_id !== populationId,
    );
    incomingSharingRules.value = incomingSharingRules.value.filter(
      (ds) => ds.population_id !== populationId,
    );
  }

  // HELPERS

  function isIncomingShare(
    share: Share,
  ): share is IncomingDataShare | IncomingSharingRule {
    return (share as IncomingDataShare).partner_org_id !== undefined;
  }

  function isOutgoingShare(
    share: Share,
  ): share is OutgoingDataShare | OutgoingSharingRule {
    return !isIncomingShare(share);
  }

  return {
    error: initError,
    ready,
    readySync,
    running,
    allIncomingSharedFields,
    incomingDataShares,
    outgoingDataShares,
    getAllIncomingDataShares,
    getIncomingDataShare,
    getDataShareWithPopulationInfo,
    getDataShareById,
    getIncomingSharingRules,
    getOutgoingSharingRules,
    outgoingSharingRuleMap,
    getSharedFieldBySourceFieldId,
    greenfieldSharesLookup,
    incomingSharingRules,
    incomingGreenfieldShares,
    isGreenfieldSharingBlocked,
    isSharingDataWithPartners,
    isSharingPopulationWithPartner,
    isSharingPopulationWithPartnerPopulation,
    getEffectiveVisibilityForPopulationWithPartner,
    outgoingRuleLookup,
    outgoingVisibilityLookup,
    outgoingOverrideCountLookup,
    outgoingOverridesLookup,
    incomingSharedFieldsLookup,
    incomingSharedFieldCountLookup,
    isOnlyOverlapCountsSharedWithPartner,
    hasMixedSharingWithPartner,
    outgoingSharingRules,
    populationSharesToServer,
    refreshDataSharesStore: refresh,
    removeAndDeleteDataShare,
    removeDataShareByPopulationId,
    toDataShareItem,
    getDefaultDataSharesMap,
    // Editing state
    editingDataShares,
    initialEditingDataShares,
    currentPopToPopMap,
    initialPopToPopMap,
    bulkPayload,
    shareIdsToDelete,
    // Editing session management
    getSessionKey,
    initializeEditingSession,
    cleanupEditingSession,
    // Editing actions
    updatePopToPopOverride,
    deletePopToPopOverride,
    bulkSaveChanges,
  };
});
